import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Hammer, Camera, Edit, Save, X, Plus, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { compressImageSafe, uploadErrorMessage } from '../../utils/imageCompression';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import UserNotesSection from '../../components/UserNotesSection';

import { getBackendUrl } from '../../utils/getBackendUrl';
import MyBadgesWidget from '../../components/MyBadgesWidget';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SubContractorProfile = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newService, setNewService] = useState('');
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    address: user.address || '',
    company_name: user.company_name || '',
    license_number: user.license_number || '',
    services_offered: user.services_offered || [],
    insurance_info: user.insurance_info || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/profile`, formData);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setIsChangingPassword(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleUploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      toast.info('Compressing image...');
      const compressedFile = await compressImageSafe(file, 512, 512, 0.85);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      await axios.post(`${API}/users/upload-profile-picture`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      onUpdate();
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    }
  };

  const addService = () => {
    if (newService.trim() && !formData.services_offered.includes(newService.trim())) {
      setFormData({ ...formData, services_offered: [...formData.services_offered, newService.trim()] });
      setNewService('');
    }
  };

  const removeService = (serviceToRemove) => {
    setFormData({ ...formData, services_offered: formData.services_offered.filter(s => s !== serviceToRemove) });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-purple-500/20 rounded-lg">
            <Hammer className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Sub Contractor Profile</h2>
            <p className="text-slate-400">Manage your business information and services</p>
          </div>
        </div>
      </div>

      {/* Badges */}
      <MyBadgesWidget />

      {/* Profile Info */}
      <div className="glass-card p-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <Avatar className="w-32 h-32">
                <AvatarImage src={user.profile_picture ? `${BACKEND_URL}${user.profile_picture}` : undefined} />
                <AvatarFallback className="bg-purple-500 text-white text-3xl">
                  {user.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                htmlFor="subcontractor-profile-picture"
                className="absolute bottom-0 right-0 bg-purple-500 p-2 rounded-full cursor-pointer hover:bg-purple-600 transition-colors"
              >
                <Camera className="w-4 h-4 text-white upload-icon-idle" />
                <input
                  id="subcontractor-profile-picture"
                  type="file"
                  accept="image/*"
                  onChange={handleUploadPicture}
                  className="hidden"
                />
              </label>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-1">{user.name}</h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-semibold">
                <Hammer className="w-4 h-4" />
                Sub Contractor
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="flex-1">
            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300">Contact Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name" className="text-slate-300">Company Name</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Your Company LLC"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="license_number" className="text-slate-300">License Number</Label>
                    <Input
                      id="license_number"
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      placeholder="LIC-123456"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-slate-300">Business Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="insurance_info" className="text-slate-300">Insurance Information</Label>
                  <Textarea
                    id="insurance_info"
                    value={formData.insurance_info}
                    onChange={(e) => setFormData({ ...formData, insurance_info: e.target.value })}
                    placeholder="Insurance provider and policy details"
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Services */}
                <div>
                  <Label className="text-slate-300 mb-2 block">Services Offered</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newService}
                      onChange={(e) => setNewService(e.target.value)}
                      placeholder="Add a service"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                    />
                    <Button type="button" onClick={addService} size="sm">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.services_offered.map((service, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-2">
                        {service}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-red-400"
                          onClick={() => removeService(service)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-gradient-to-r from-purple-500 to-purple-600">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Email</p>
                    <p className="text-white font-semibold">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Company Name</p>
                    <p className="text-white font-semibold">{user.company_name || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">License Number</p>
                    <p className="text-white font-semibold">{user.license_number || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Phone</p>
                    <p className="text-white font-semibold">{user.phone || 'Not provided'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-slate-400 text-sm mb-1">Business Address</p>
                  <p className="text-white font-semibold">{user.address || 'Not provided'}</p>
                </div>

                {user.insurance_info && (
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Insurance Information</p>
                    <p className="text-white font-semibold">{user.insurance_info}</p>
                  </div>
                )}

                {/* Services */}
                {user.services_offered && user.services_offered.length > 0 && (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">Services Offered</p>
                    <div className="flex flex-wrap gap-2">
                      {user.services_offered.map((service, index) => (
                        <Badge key={index} variant="secondary">{service}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-gradient-to-r from-purple-500 to-purple-600"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Info */}
      <div className="glass-card p-6">
        <h3 className="text-xl font-bold text-white mb-4">Business Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">Licensed & Insured</p>
            <p className="text-slate-400 text-sm">Professional contractor services</p>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">Specialized Services</p>
            <p className="text-slate-400 text-sm">{user.services_offered?.length || 0} service types offered</p>
          </div>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white">Change Password</h3>
        </div>

        {!isChangingPassword ? (
          <Button
            onClick={() => setIsChangingPassword(true)}
            className="bg-gradient-to-r from-red-500 to-red-600"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </Button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current_password" className="text-slate-300">Current Password</Label>
              <PasswordInput
                id="current_password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new_password" className="text-slate-300">New Password</Label>
              <PasswordInput
                id="new_password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-slate-300">Confirm New Password</Label>
              <PasswordInput
                id="confirm_password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-gradient-to-r from-red-500 to-red-600">
                <Save className="w-4 h-4 mr-2" />
                Update Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Admin Messages Section - Temporarily disabled due to React hook compatibility issues */}
      {/* TODO: Re-architect UserNotesSection component */}
      {/* <UserNotesSection userId={user.id} /> */}
    </div>
  );
};

export default SubContractorProfile;